import { gradients, type GradientKey } from '../gradients/library.js';
import { dominantStop, gradientToCss } from '../gradients/render.js';
import type { Gradient } from '../gradients/types.js';
import { isDark as isDarkColor, relativeLuminance, pickContrastingColor } from './contrast.js';
import { darken, lighten, rotateHue } from './manipulate.js';
import { withAlpha } from './opacity.js';
import { glass as glassStyle, type GlassStyle } from './glass.js';

export type SurfaceInput =
  | { color: string }
  | { gradient: GradientKey | Gradient };

export interface SurfaceColors {
  background: string;
  backgroundStructured: Gradient | null;
  text: string;
  textMuted: string;
  textSubtle: string;
  secondary: string;
  tertiary: string;
  border: string;
  hover: string;
  active: string;
  ghost: string;
  focusRing: string;
  glass: GlassStyle;
  isDark: boolean;
  luminance: number;
}

function resolveSource(input: SurfaceInput): { background: string; structured: Gradient | null; reference: string } {
  if ('color' in input) {
    return { background: input.color, structured: null, reference: input.color };
  }
  const g = typeof input.gradient === 'string' ? gradients[input.gradient] : input.gradient;
  if (!g) throw new Error(`Unknown gradient: ${String(input.gradient)}`);
  return { background: gradientToCss(g), structured: g, reference: dominantStop(g) };
}

export function deriveSurfaceColors(input: SurfaceInput): SurfaceColors {
  const { background, structured, reference } = resolveSource(input);
  const dark = isDarkColor(reference);
  const luminance = relativeLuminance(reference);

  const text = pickContrastingColor(reference);
  const textMuted = withAlpha(text, 0.7);
  const textSubtle = withAlpha(text, 0.45);

  const secondary = rotateHue(reference, 30);
  const tertiary = rotateHue(reference, 120);

  const border = dark ? lighten(reference, 0.12) : darken(reference, 0.12);
  const hover = dark ? lighten(reference, 0.06) : darken(reference, 0.06);
  const active = dark ? lighten(reference, 0.12) : darken(reference, 0.12);
  const ghost = withAlpha(reference, 0.08);
  const focusRing = withAlpha(reference, 0.5);
  const glass = glassStyle(reference);

  return {
    background,
    backgroundStructured: structured,
    text,
    textMuted,
    textSubtle,
    secondary,
    tertiary,
    border,
    hover,
    active,
    ghost,
    focusRing,
    glass,
    isDark: dark,
    luminance
  };
}
